/*
 * lazy-section.js
 *
 * Builds heavy below-the-fold section markup only when the user gets close.
 *
 * Markup contract (see sections/relacionados.liquid):
 *
 *   <div data-lazy-root id="section-x">             wrapper, always in the DOM
 *     ...cheap markup (headings, nav buttons)...
 *     <div data-lazy-placeholder>...</div>          optional, reserves the height
 *     <template data-lazy-section>...heavy...</template>
 *   </div>
 *
 * When the wrapper gets within `rootMargin` of the viewport, the placeholder is
 * removed and the template content is inserted in its place. Then a bubbling
 * `lazy-section:hydrated` CustomEvent is dispatched on the wrapper so the
 * section scripts can initialise against the real nodes.
 *
 * Optional attributes on the wrapper:
 *   data-lazy-margin="600px 0px"   override the IntersectionObserver rootMargin
 *
 * The wrapper itself is observed (not the placeholder) so it keeps working when
 * the wrapper uses content-visibility: auto and its contents are skipped.
 * Hydration is idempotent. Without IntersectionObserver everything hydrates
 * immediately.
 */
(function () {
  'use strict';

  if (window.VardoLazySection) return;

  var EVENT_NAME = 'lazy-section:hydrated';
  var observers = {};

  function defaultMargin() {
    var isMobile =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 749px)').matches;
    return isMobile ? '600px 0px' : '800px 0px';
  }

  function hydrate(root) {
    if (!root) return false;
    var templates = root.querySelectorAll('template[data-lazy-section]');
    if (!templates.length) return false;

    for (var i = 0; i < templates.length; i++) {
      var tpl = templates[i];
      var parent = tpl.parentNode;
      if (!parent) continue;

      var prev = tpl.previousElementSibling;
      if (prev && prev.hasAttribute('data-lazy-placeholder')) {
        prev.parentNode.removeChild(prev);
      }

      parent.insertBefore(document.importNode(tpl.content, true), tpl);
      parent.removeChild(tpl);
    }

    root.setAttribute('data-lazy-hydrated', '');
    root.dispatchEvent(
      new CustomEvent(EVENT_NAME, { bubbles: true, detail: { root: root } })
    );
    return true;
  }

  function getObserver(margin) {
    if (observers[margin]) return observers[margin];
    observers[margin] = new IntersectionObserver(
      function (entries, observer) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          observer.unobserve(entries[i].target);
          hydrate(entries[i].target);
        }
      },
      { rootMargin: margin }
    );
    return observers[margin];
  }

  function observe(root) {
    if (!root.querySelector('template[data-lazy-section]')) return;
    if (typeof IntersectionObserver !== 'function') {
      hydrate(root);
      return;
    }
    getObserver(root.getAttribute('data-lazy-margin') || defaultMargin()).observe(
      root
    );
  }

  function scan(scope) {
    scope = scope || document;
    var roots = scope.querySelectorAll('[data-lazy-root]');
    for (var i = 0; i < roots.length; i++) observe(roots[i]);
    if (
      scope !== document &&
      scope.hasAttribute &&
      scope.hasAttribute('data-lazy-root')
    ) {
      observe(scope);
    }
  }

  window.VardoLazySection = { hydrate: hydrate, scan: scan, EVENT: EVENT_NAME };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  } else {
    scan(document);
  }

  // Theme editor: re-rendered sections come without templates (design mode),
  // but scanning is harmless and keeps the contract symmetric.
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });
})();
